import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { authApi } from '@/services/api/endpoints';
import { onAuthFailure, tokenStorage } from '@/services/api/client';
import { useAuthStore } from '@/stores/auth.store';
import { useMergeFavorites } from './useFavorites';

/**
 * Uygulama açılışında saklı token varsa oturumu doğrular.
 * Token geçersizse sessizce temizlenir — kullanıcıya hata gösterilmez.
 */
export function useAuthBootstrap(): void {
  const setUser = useAuthStore((state) => state.setUser);
  const markResolved = useAuthStore((state) => state.markResolved);
  const clear = useAuthStore((state) => state.clear);

  useEffect(() => {
    const unsubscribe = onAuthFailure(() => clear());
    return () => {
      unsubscribe();
    };
  }, [clear]);

  useEffect(() => {
    let cancelled = false;

    if (!tokenStorage.getAccess()) {
      markResolved();
      return;
    }

    authApi
      .me()
      .then((user) => {
        if (!cancelled) {
          setUser(user);
          markResolved();
        }
      })
      .catch(() => {
        if (!cancelled) {
          clear();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [setUser, markResolved, clear]);
}

export function useLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();
  const mergeFavorites = useMergeFavorites();

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: async (result) => {
      // Önce ÖNBELLEK TEMİZLENİR: bir önceki kullanıcının siparişleri,
      // adresleri veya favorileri yeni oturumda bir an için bile
      // görünmemelidir. invalidate yetmez — eski veri tazelenene kadar
      // ekranda kalırdı.
      queryClient.clear();
      setSession(result);

      // Cihazdaki favoriler hesaba taşınır (idempotent).
      await mergeFavorites();
      await queryClient.invalidateQueries();
    },
  });
}

/**
 * Kayıt. Başarılı kayıt doğrudan oturum açar — kullanıcıyı hemen ardından
 * bir de giriş formuna göndermek gereksiz sürtünmedir.
 */
export function useRegister() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();
  const mergeFavorites = useMergeFavorites();

  return useMutation({
    mutationFn: authApi.register,
    onSuccess: async (result) => {
      queryClient.clear();
      setSession(result);

      await mergeFavorites();
      await queryClient.invalidateQueries();
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = tokenStorage.getRefresh();

      if (refreshToken) {
        // Sunucu tarafı iptal başarısız olsa da yerel oturum kapatılır.
        await authApi.logout(refreshToken).catch(() => undefined);
      }
    },
    onSettled: () => {
      clear();
      queryClient.clear();
    },
  });
}
